// Button Component
// Premium button with multiple variants, loading states, and animations

import * as React from 'react';
import { Button as MuiButton, ButtonProps, styled } from '@mui/material';
import { motion, HTMLMotionProps } from 'framer-motion';

export interface ButtonProps extends Omit<ButtonProps, 'variant' | 'children'> {
  variant?: 'contained' | 'outlined' | 'text' | 'soft' | 'ghost' | 'gradient';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  disableAnimation?: boolean;
}

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) =>
    !['loading', 'loadingText', 'leftIcon', 'rightIcon', 'disableAnimation'].includes(prop as string),
})<ButtonProps>(({ theme, loading, disableAnimation }) => ({
  position: 'relative',
  minHeight: loading ? 40 : undefined,
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1.5),
  },
  '& .MuiButton-endIcon': {
    marginLeft: theme.spacing(1.5),
  },
  // Disable transform on loading
  ...(loading && !disableAnimation && {
    '&:hover': {
      transform: 'none',
    },
    '&:active': {
      transform: 'none',
    },
  }),
}));

const Spinner = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: 18,
  height: 18,
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
  borderTopColor: theme.palette.getContrastText(theme.palette.primary.main),
  borderRadius: '50%',
  animation: 'spin 0.6s linear infinite',
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' },
  },
}));

const ButtonContent = styled('span')(({ theme, loading }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: loading ? 0 : 1,
  visibility: loading ? 'hidden' : 'visible',
  transition: 'opacity 0.2s ease, visibility 0.2s ease',
}));

const LoadingContent = styled('span')(({ theme }) => ({
  position: 'absolute',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  color: 'inherit',
}));

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'contained',
      size = 'medium',
      loading = false,
      loadingText,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      className,
      disableAnimation = false,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <StyledButton
        ref={ref}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={isDisabled}
        disableElevation={variant !== 'contained'}
        disableAnimation={disableAnimation}
        className={className}
        {...props}
      >
        {!loading && leftIcon && (
          <span className="MuiButton-startIcon">{leftIcon}</span>
        )}
        <ButtonContent loading={loading}>{children}</ButtonContent>
        {!loading && rightIcon && (
          <span className="MuiButton-endIcon">{rightIcon}</span>
        )}
        {loading && (
          <LoadingContent>
            <Spinner />
            {loadingText && <span style={{ marginLeft: 8 }}>{loadingText}</span>}
          </LoadingContent>
        )}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

// Icon Button
export interface IconButtonProps extends Omit<React.ComponentPropsWithoutRef<typeof MuiButton>, 'variant' | 'size'> {
  variant?: 'contained' | 'outlined' | 'text' | 'soft' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  'aria-label': string;
  children: React.ReactNode;
  className?: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = 'soft', size = 'medium', loading = false, 'aria-label': ariaLabel, children, className, ...props }, ref) => {
    return (
      <StyledButton
        ref={ref}
        variant={variant}
        size={size}
        disabled={loading}
        disableElevation={variant !== 'contained'}
        aria-label={ariaLabel}
        className={className}
        style={{ padding: 0, minWidth: size === 'small' ? 32 : size === 'large' ? 48 : 40, ...props.style }}
        {...props}
      >
        {loading ? <Spinner /> : children}
      </StyledButton>
    );
  }
);

IconButton.displayName = 'IconButton';

// Floating Action Button
export interface FabProps extends Omit<React.ComponentPropsWithoutRef<typeof MuiButton>, 'variant' | 'size'> {
  size?: 'small' | 'medium' | 'large' | 'extended';
  variant?: 'contained' | 'gradient';
  children: React.ReactNode;
  className?: string;
}

export const Fab = React.forwardRef<HTMLButtonElement, FabProps>(
  ({ size = 'medium', variant = 'contained', children, className, ...props }, ref) => {
    const sizes = {
      small: { width: 40, height: 40, fontSize: '1.25rem' },
      medium: { width: 56, height: 56, fontSize: '1.5rem' },
      large: { width: 72, height: 72, fontSize: '1.75rem' },
      extended: { width: 'auto', height: 56, fontSize: '1rem', padding: '0 24px', borderRadius: 28 },
    };

    return (
      <StyledButton
        ref={ref}
        variant={variant}
        disableElevation={variant !== 'contained'}
        className={className}
        style={{
          width: sizes[size].width,
          height: sizes[size].height,
          fontSize: sizes[size].fontSize,
          padding: sizes[size].padding,
          borderRadius: sizes[size].borderRadius ?? 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          ...props.style,
        }}
        {...props}
      >
        {children}
      </StyledButton>
    );
  }
);

Fab.displayName = 'Fab';

// Button Group
export interface ButtonGroupProps {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text' | 'soft' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  orientation?: 'horizontal' | 'vertical';
  spacing?: number;
  className?: string;
}

export const ButtonGroup = ({
  children,
  variant = 'outlined',
  size = 'medium',
  orientation = 'horizontal',
  spacing = 2,
  className,
}: ButtonGroupProps) => {
  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        flexDirection: orientation === 'horizontal' ? 'row' : 'column',
        gap: spacing,
        borderRadius: 12,
        overflow: 'hidden',
      }}
      role="group"
    >
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        const isFirst = index === 0;
        const isLast = index === React.Children.count(children) - 1;
        return React.cloneElement(child as React.ReactElement<ButtonProps>, {
          variant,
          size,
          style: {
            borderRadius: orientation === 'horizontal'
              ? isFirst
                ? { topLeft: 12, bottomLeft: 12, topRight: 0, bottomRight: 0 }
                : isLast
                ? { topRight: 12, bottomRight: 12, topLeft: 0, bottomLeft: 0 }
                : 0
              : isFirst
              ? { topLeft: 12, topRight: 12, bottomLeft: 0, bottomRight: 0 }
              : isLast
              ? { bottomLeft: 12, bottomRight: 12, topLeft: 0, topRight: 0 }
              : 0,
            margin: 0,
            ...child.props.style,
          },
        });
      })}
    </div>
  );
};

// Toggle Button
export interface ToggleButtonProps extends Omit<ButtonProps, 'variant'> {
  selected?: boolean;
  onChange?: (selected: boolean) => void;
}

export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ selected = false, onChange, variant = 'outlined', children, ...props }, ref) => {
    return (
      <StyledButton
        ref={ref}
        variant={selected ? 'contained' : variant}
        onClick={() => onChange?.(!selected)}
        {...props}
      >
        {children}
      </StyledButton>
    );
  }
);

ToggleButton.displayName = 'ToggleButton';

// Split Button
export interface SplitButtonProps {
  primaryAction: React.ReactNode;
  primaryOnClick: () => void;
  secondaryActions: Array<{ label: React.ReactNode; onClick: () => void; icon?: React.ReactNode }>;
  variant?: 'contained' | 'outlined' | 'soft' | 'ghost';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const SplitButton = ({
  primaryAction,
  primaryOnClick,
  secondaryActions,
  variant = 'contained',
  size = 'medium',
  className,
}: SplitButtonProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <div className={className} style={{ display: 'inline-flex', position: 'relative' }}>
      <StyledButton variant={variant} size={size} onClick={primaryOnClick} disableElevation={variant !== 'contained'}>
        {primaryAction}
      </StyledButton>
      <StyledButton
        variant={variant}
        size={size}
        onClick={() => setOpen(!open)}
        disableElevation={variant !== 'contained'}
        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '0 12px' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </StyledButton>
      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: 8,
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: 'var(--anime-token-surface-default)',
              border: '1px solid var(--anime-token-border-default)',
              borderRadius: 12,
              boxShadow: 'var(--anime-token-shadow-xl)',
              minWidth: 160,
              overflow: 'hidden',
            }}
          >
            {secondaryActions.map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  action.onClick();
                  setOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--anime-token-text-primary)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  borderBottom: index < secondaryActions.length - 1 ? '1px solid var(--anime-token-divider)' : 'none',
                  '&:hover': {
                    background: 'var(--anime-token-surface-hover)',
                  },
                }}
              >
                {action.icon && <span>{action.icon}</span>}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Button;